import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { ArrowLeftRight, ChevronRight, Search, SlidersHorizontal, User } from 'lucide-react';
import { UserMenu } from '@/components/UserMenu';
import { ThemeToggle } from '@/components/ThemeToggle';
import { HeaderSearch } from '@/components/header/HeaderSearch';
import { HeaderQuickActions } from '@/components/header/HeaderQuickActions';
import { HeaderNotifications } from '@/components/header/HeaderNotifications';
import { getPrimaryQuickAction, matchRouteByPathname } from '@/components/header/header-config';
import type { HeaderQuickActionConfig } from '@/components/header/header-config';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useAdminHeaderNotifications } from '@/hooks/queries/useHeaderNotifications';
import { adminPath, ownerPath } from '@/lib/hosts';

function resolveAdminTitle(
  pathname: string,
  fallback: string,
  tAdmin: (key: string) => string,
): string {
  const matched = matchRouteByPathname(pathname, 'admin');
  if (!matched) return fallback;
  return matched.label.namespace === 'admin' ? tAdmin(matched.label.key) : fallback;
}

function translateActionLabel(
  action: HeaderQuickActionConfig,
  tCommon: (key: string) => string,
  tNav: (key: string) => string,
  tAdmin: (key: string) => string,
): string {
  if (action.label.namespace === 'navigation') return tNav(action.label.key);
  if (action.label.namespace === 'admin') return tAdmin(action.label.key);
  return tCommon(action.label.key);
}

function openCommandPalette() {
  document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
}

export function AdminHeader() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t: tCommon } = useTranslation('common');
  const { t: tNav } = useTranslation('navigation');
  const { t: tAdmin } = useTranslation('admin');
  const title = useMemo(
    () => resolveAdminTitle(pathname, tAdmin('administration'), tAdmin),
    [pathname, tAdmin],
  );

  const primaryAction = useMemo(() => getPrimaryQuickAction('admin'), []);
  const PrimaryActionIcon = primaryAction?.icon;
  const primaryActionLabel = useMemo(
    () => (primaryAction ? translateActionLabel(primaryAction, tCommon, tNav, tAdmin) : null),
    [primaryAction, tCommon, tNav, tAdmin],
  );

  const notifications = useAdminHeaderNotifications();

  return (
    <header className="sticky top-0 z-30 border-b border-neutral-200 dark:border-charcoal-700 bg-white/95 dark:bg-charcoal-900/95 backdrop-blur">
      {/* Desktop (lg+) — single line, 44px */}
      <div className="hidden h-[var(--topbar-height)] items-center gap-3 px-4 lg:flex">
        {/* Breadcrumb gauche */}
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[13px] font-medium text-blue-600 truncate">Administration</span>
          <ChevronRight className="h-3 w-3 shrink-0 text-charcoal-300" />
          <span className="text-[13px] font-semibold text-charcoal-900 truncate">{title}</span>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Actions droite */}
        <div className="flex items-center gap-2">
          <Button asChild size="sm" variant="outline" className="h-8 gap-1.5 text-[13px]">
            <Link to={ownerPath('/dashboard')}>
              <ArrowLeftRight className="h-3.5 w-3.5" />
              <span className="hidden xl:inline">{tAdmin('back_to_app')}</span>
            </Link>
          </Button>

          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={openCommandPalette}>
            <Search className="h-4 w-4" />
          </Button>

          {primaryAction && primaryActionLabel && PrimaryActionIcon && (
            <Button
              size="sm"
              className="h-8 gap-1.5 text-[13px]"
              onClick={() => navigate(primaryAction.path)}
            >
              <PrimaryActionIcon className="h-3.5 w-3.5" />
              {primaryActionLabel}
            </Button>
          )}

          <ThemeToggle />

          <HeaderNotifications
            items={notifications.items}
            unreadCount={notifications.unreadCount}
            isLoading={notifications.isLoading}
            onMarkAsRead={notifications.markAsRead}
            onMarkAllAsRead={notifications.markAllAsRead}
          />

          <UserMenu profileUrl="/profile" />
        </div>
      </div>

      {/* Mobile (<lg) */}
      <div className="flex h-14 items-center gap-2 px-3 pl-14 lg:hidden">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[11px] font-bold uppercase tracking-[0.15em] text-blue-700">
            Administration
          </p>
          <p className="truncate font-heading text-base font-semibold text-charcoal-900">{title}</p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <HeaderNotifications
            items={notifications.items}
            unreadCount={notifications.unreadCount}
            isLoading={notifications.isLoading}
            onMarkAsRead={notifications.markAsRead}
            onMarkAllAsRead={notifications.markAllAsRead}
          />

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button type="button" variant="outline" size="icon" className="h-9 w-9 rounded-lg">
                <SlidersHorizontal className="size-4" />
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[92vw] max-w-sm gap-0 p-0" showCloseButton>
              <SheetHeader className="border-b border-charcoal-200 dark:border-charcoal-700">
                <SheetTitle>{tCommon('header_mobile_panel_title')}</SheetTitle>
                <SheetDescription>{tCommon('header_mobile_panel_desc')}</SheetDescription>
              </SheetHeader>

              <div className="flex-1 space-y-5 overflow-y-auto p-4">
                <HeaderSearch mode="admin" />

                {primaryAction && primaryActionLabel && PrimaryActionIcon && (
                  <Button
                    type="button"
                    className="w-full justify-start"
                    onClick={() => {
                      setMobileOpen(false);
                      navigate(primaryAction.path);
                    }}
                  >
                    <PrimaryActionIcon className="size-4" />
                    {primaryActionLabel}
                  </Button>
                )}

                <div className="space-y-2">
                  <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-charcoal-500">
                    {tCommon('header_quick_actions')}
                  </p>
                  <HeaderQuickActions
                    mode="admin"
                    layout="panel"
                    onAction={() => setMobileOpen(false)}
                  />
                </div>

                <div className="space-y-2">
                  <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-charcoal-500">
                    {tCommon('appearance')}
                  </p>
                  <ThemeToggle size="md" />
                </div>

                <div className="grid gap-2 border-t border-charcoal-200 dark:border-charcoal-700 pt-4">
                  <Button
                    asChild
                    variant="outline"
                    onClick={() => setMobileOpen(false)}
                    className="justify-start"
                  >
                    <Link to={adminPath('/profile')}>
                      <User className="size-4" />
                      {tAdmin('my_profile')}
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    onClick={() => setMobileOpen(false)}
                    className="justify-start"
                  >
                    <Link to={ownerPath('/dashboard')}>
                      <ArrowLeftRight className="size-4" />
                      {tAdmin('back_to_app')}
                    </Link>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
